import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtGHS, fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, PackageOpen } from "lucide-react";
import { channelLabel, orderBadge, statusLabel, TypeTag } from "./common";
import { OrderFormDialog } from "./OrderFormDialog";
import { OrderDetailDialog } from "./OrderDetailDialog";

export const OrdersTab = ({ hospitals, onChanged, can, openId, setOpenId }) => {
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [ownId, setOwnId] = useState(null);
  const detailId = setOpenId ? openId : ownId;
  const setDetailId = setOpenId || setOwnId;

  const load = useCallback(() => {
    api.get("/wholesale/orders", { params: status === "all" ? {} : { status } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged(); };

  return (
    <Card className="p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 h-9" data-testid="ws-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending / Pending Delivery</SelectItem>
            <SelectItem value="dispatched">Dispatched</SelectItem>
            <SelectItem value="pending_review">Pending Review</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        {can("wholesale.orders") && <Button data-testid="ws-new-order-btn" onClick={() => setForm({})}><Plus className="h-4 w-4 mr-1.5" /> New supply order</Button>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr><th className="py-2.5 px-3">Order</th><th className="py-2.5 px-3">Hospital Partner</th><th className="py-2.5 px-3">Type / supplier</th><th className="py-2.5 px-3">Channel / ref</th>
              <th className="py-2.5 px-3 text-right">Items</th><th className="py-2.5 px-3 text-right">Total (cost)</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3">Captured</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><PackageOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />No supply orders yet</td></tr>}
            {rows.map((o) => (
              <tr key={o.id} className="border-b border-border/60 hover:bg-muted/40 cursor-pointer" onClick={() => setDetailId(o.id)} data-testid={`ws-order-row-${o.code}`}>
                <td className="py-2 px-3 font-mono text-xs">{o.code}</td>
                <td className="py-2 px-3 font-medium">{o.hospital_name}</td>
                <td className="py-2 px-3"><TypeTag t={o.fulfilment_type} testId={`ws-order-type-${o.code}`} /><span className="ml-1.5 text-xs text-muted-foreground">{o.supplier_name}</span></td>
                <td className="py-2 px-3 text-muted-foreground">{channelLabel(o.channel)}{o.external_ref ? ` · ${o.external_ref}` : ""}</td>
                <td className="py-2 px-3 text-right font-mono">{o.lines.length}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(o.total_cost)}</td>
                <td className="py-2 px-3"><StatusBadge value={orderBadge(o.status)} label={statusLabel(o)} data-testid={`ws-order-status-${o.code}`} /></td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(o.created_at)} · {o.created_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OrderFormDialog open={!!form} order={form?.id ? form : null} hospitals={hospitals} onClose={() => setForm(null)} onSaved={changed} />
      <OrderDetailDialog id={detailId} can={can} onClose={() => setDetailId(null)} onChanged={changed} onEdit={(o) => { setDetailId(null); setForm(o); }} />
    </Card>
  );
};
